<template>
  <transition name="dchart-fade">
    <div
      v-if="show"
      class="dchart-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dchart-title"
      @mousedown.self="close"
    >
      <div class="dchart-panel" @mousedown.stop>
        <button type="button" class="dchart-close" aria-label="Close" @click="close">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div class="dchart-header">
          <span class="dchart-header__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <line x1="12" y1="7" x2="12.01" y2="7" />
            </svg>
          </span>
          <h3 id="dchart-title" class="dchart-header__title">Discount Chart</h3>
        </div>

        <p class="dchart-intro">
          Quantity-based discount tiers configured for each organisation — the org-wide
          <em>All products</em> chart plus any product-specific overrides. The eligible-discount
          toggle uses the highest <em>Min Qty</em> the order meets. Each chart below is a separate
          pool: a product listed under its own chart or a group chart follows <strong>only</strong>
          that chart, and its units never count toward the <em>All products</em> quantity. Within a
          group, every member's bottles are added together before matching the tier.
        </p>

        <div v-if="errorMessage" class="dchart-alert">{{ errorMessage }}</div>

        <div v-if="isLoading" class="dchart-empty">Loading tiers…</div>

        <div v-else class="dchart-list">
          <section
            v-for="org in organisationSections"
            :key="org.id"
            class="dchart-org"
            :class="{ 'is-current': isCurrentOrg(org.id) }"
          >
            <header class="dchart-org__head">
              <h4>{{ org.name }}</h4>
              <span v-if="isCurrentOrg(org.id)" class="dchart-current-pill">Current</span>
            </header>
            <div v-if="!org.blocks.length" class="dchart-org__empty">No tiers configured.</div>
            <template v-else>
              <div v-for="block in org.blocks" :key="block.key" class="dchart-scope">
                <div class="dchart-scope__head">
                  <span class="dchart-scope__title">{{ block.title }}</span>
                  <span v-if="block.sku" class="dchart-scope__sku">{{ block.sku }}</span>
                </div>
                <p v-if="block.members.length" class="dchart-scope__members">
                  Quantities pool across: {{ block.members.join(', ') }}
                </p>
                <div class="dchart-table-wrap">
                  <table class="dchart-table">
                    <thead>
                      <tr>
                        <th class="dchart-col-qty">Min Qty</th>
                        <th class="dchart-col-mode">Mode</th>
                        <th class="dchart-col-value">Value</th>
                        <th class="dchart-col-label">Label</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="tier in block.tiers" :key="tier.id">
                        <td><strong>{{ tier.min_quantity }}+</strong></td>
                        <td>
                          <span :class="['dchart-pill', tier.mode === 'percentage' ? 'dchart-pill--blue' : 'dchart-pill--amber']">
                            {{ tier.mode === 'percentage' ? 'Percentage' : 'Flat / unit' }}
                          </span>
                        </td>
                        <td>
                          <strong class="dchart-value">{{ tier.mode === 'percentage' ? `${tier.value}%` : `₹${tier.value}` }}</strong>
                          <small v-if="tier.mode === 'flat_per_unit'" class="dchart-sub">per unit</small>
                        </td>
                        <td class="dchart-label-cell">{{ tier.label || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </section>
        </div>

        <div class="dchart-actions">
          <button type="button" class="dchart-btn" @click="close">Got it</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import { ORGANISATION_ID_TO_NAME } from '@/views/users/userHelpers.js'

// Manakkal's three transacting orgs (Agropharma / Drugs / Vet Pharma).
// Mirrors POS.vue's ALLOWED_ORG_IDS — MCCI Patients C/O (id 3) is an
// internal cost-centre, not a real sales channel, so it's omitted from
// the chart.
const CHART_ORG_IDS = [1, 2, 4]

export default {
  name: 'DiscountChartModal',
  props: {
    show: { type: Boolean, default: false },
    // Pass the form's active org id (TeleSales / OrderForm) or null when the
    // banner spans multiple orgs (POS multi-org cart). Drives the "Current" pill.
    currentOrganisationId: { type: [Number, String], default: null }
  },
  data () {
    return {
      isLoading: false,
      errorMessage: '',
      presetsByOrg: {}
    }
  },
  computed: {
    organisationSections () {
      const byMinQty = (a, b) => Number(a.min_quantity) - Number(b.min_quantity)
      return CHART_ORG_IDS.map((orgId) => {
        const name = ORGANISATION_ID_TO_NAME[orgId] || `Organisation #${orgId}`
        const all = (Array.isArray(this.presetsByOrg[orgId]) ? this.presetsByOrg[orgId] : [])
          .filter((t) => Boolean(t.active))

        // Each org renders as a list of "scope blocks": the org-wide chart
        // first ("All products"), then one block per product group, then one
        // per product that charts on its own. Each block is a separate pool
        // — its tiers replace the org-wide chart for those lines rather than
        // stacking on top — so they're shown as separate tables, not merged.
        const blocks = []

        const orgTiers = all.filter((t) => !t.item_id && !t.group_key).sort(byMinQty)
        if (orgTiers.length) {
          blocks.push({ key: 'org-wide', title: 'All products', sku: '', members: [], tiers: orgTiers })
        }

        // A group's ladder is stored once per member product (identical rows
        // across all of them), so collapse to one table keyed by min qty and
        // list the members instead of repeating the same chart N times.
        const groupMap = {}
        all.filter((t) => t.group_key).forEach((t) => {
          const key = String(t.group_key)
          if (!groupMap[key]) {
            groupMap[key] = { key: `group-${key}`, title: `${key} products`, sku: '', members: [], tierByQty: {} }
          }
          const qty = Number(t.min_quantity)
          if (!groupMap[key].tierByQty[qty]) groupMap[key].tierByQty[qty] = t
          const memberName = (t.item && t.item.name) || (t.item_id ? `Item #${t.item_id}` : '')
          if (memberName && !groupMap[key].members.includes(memberName)) {
            groupMap[key].members.push(memberName)
          }
        })
        Object.values(groupMap)
          .map((g) => ({
            key: g.key,
            title: g.title,
            sku: '',
            members: g.members.slice().sort((a, b) => a.localeCompare(b)),
            tiers: Object.values(g.tierByQty).sort(byMinQty)
          }))
          .sort((a, b) => a.title.localeCompare(b.title))
          .forEach((g) => blocks.push(g))

        const productMap = {}
        all.filter((t) => t.item_id && !t.group_key).forEach((t) => {
          const itemId = Number(t.item_id)
          if (!productMap[itemId]) {
            productMap[itemId] = {
              key: `item-${itemId}`,
              title: (t.item && t.item.name) || `Item #${itemId}`,
              sku: (t.item && t.item.sku) || '',
              members: [],
              tiers: []
            }
          }
          productMap[itemId].tiers.push(t)
        })
        Object.values(productMap)
          .map((group) => ({ ...group, tiers: group.tiers.sort(byMinQty) }))
          .sort((a, b) => a.title.localeCompare(b.title))
          .forEach((group) => blocks.push(group))

        return { id: orgId, name, blocks }
      })
    }
  },
  watch: {
    show (next) {
      if (next) {
        this.loadAll()
        document.addEventListener('keydown', this.handleKeydown)
      } else {
        document.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    close () {
      this.$emit('update:show', false)
    },
    handleKeydown (e) {
      if (e.key === 'Escape') this.close()
    },
    isCurrentOrg (id) {
      return Number(this.currentOrganisationId || 0) === Number(id)
    },
    loadAll () {
      this.isLoading = true
      this.errorMessage = ''
      const base = this.$apiAdress
      Promise.all(
        CHART_ORG_IDS.map((orgId) =>
          axios.get(`${base}/api/discount-presets/by-org/${orgId}`)
            .then((res) => ({ orgId, rows: Array.isArray(res.data) ? res.data : [] }))
            .catch(() => ({ orgId, rows: [] }))
        )
      )
        .then((results) => {
          const map = {}
          results.forEach(({ orgId, rows }) => { map[orgId] = rows })
          this.presetsByOrg = map
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || 'Unable to load discount tiers.'
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.dchart-overlay {
  position: fixed;
  inset: 0;
  /* Sits above POS checkout (1100) and CoreUI CModal (1050). Matches
     ShippingTariffModal's 1300 so the two reference dialogs stack the same. */
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.dchart-panel {
  position: relative;
  width: 100%;
  max-width: 760px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.4rem 1.4rem 1.15rem;
  animation: dchart-pop 0.28s cubic-bezier(0.18, 1.2, 0.4, 1) both;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
}

.dchart-close {
  position: absolute;
  top: 0.55rem;
  right: 0.55rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.dchart-close:hover { background: #f3f4f6; color: #1f2a44; }
.dchart-close:focus { outline: none; }
.dchart-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.dchart-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.7rem;
  padding-right: 1.8rem;
}

.dchart-header__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ecfdf5;
  color: #0e5b46;
  flex: 0 0 auto;
}

.dchart-header__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
}

.dchart-intro {
  margin: 0 0 0.85rem;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.45;
}

.dchart-intro em {
  font-style: normal;
  color: #0e5b46;
  font-weight: 600;
}

.dchart-alert {
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.85rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

.dchart-empty {
  padding: 1.2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.85rem;
  background: #fbfcfe;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  margin-bottom: 0.85rem;
}

.dchart-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.dchart-org {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

.dchart-org.is-current {
  border-color: #16a07a;
  box-shadow: 0 0 0 1px rgba(22, 160, 122, 0.18);
}

.dchart-org__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.6rem 0.85rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.dchart-org__head h4 {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.dchart-current-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #dff5ea;
  color: #0e5b46;
}

.dchart-org__empty {
  padding: 0.8rem 0.95rem;
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.dchart-scope + .dchart-scope {
  border-top: 1px solid #eef1f7;
}

.dchart-scope__head {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem 0.1rem;
}

.dchart-scope__title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: 0.01em;
}

.dchart-scope__sku {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
}

.dchart-scope__members {
  margin: 0 0 0.45rem;
  font-size: 0.72rem;
  line-height: 1.45;
  color: #6b7280;
}

.dchart-table-wrap {
  overflow-x: auto;
}

.dchart-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.dchart-table th,
.dchart-table td {
  padding: 0.55rem 0.85rem;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.dchart-table tbody tr:last-child td {
  border-bottom: 0;
}

.dchart-table th {
  background: #f8fafc;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.dchart-col-qty   { width: 90px; }
.dchart-col-mode  { width: 130px; }
.dchart-col-value { width: 140px; }

.dchart-value { color: #0e5b46; }

.dchart-sub {
  margin-left: 0.35rem;
  font-size: 0.7rem;
  color: #6b7280;
}

.dchart-label-cell {
  color: #4b5563;
}

.dchart-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.dchart-pill--blue  { background: #dbeafe; color: #1e40af; }
.dchart-pill--amber { background: #fef3c7; color: #92400e; }

.dchart-actions {
  display: flex;
  justify-content: flex-end;
}

.dchart-btn {
  height: 38px;
  min-width: 110px;
  padding: 0 1.1rem;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.dchart-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.04);
}

.dchart-btn:active { transform: translateY(0); filter: brightness(0.96); }
.dchart-btn:focus { outline: none; }
.dchart-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.3);
}

.dchart-fade-enter-active,
.dchart-fade-leave-active { transition: opacity 0.18s ease; }
.dchart-fade-enter,
.dchart-fade-leave-to { opacity: 0; }

@keyframes dchart-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.97); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@media (max-width: 575px) {
  .dchart-panel { padding: 1.1rem 1rem 0.95rem; }
  .dchart-table th,
  .dchart-table td { padding: 0.5rem 0.6rem; }
  .dchart-col-qty   { width: 70px; }
  .dchart-col-mode  { width: 100px; }
  .dchart-col-value { width: 110px; }
}
</style>
